import { useState, useCallback } from "react";
import html2canvas from "html2canvas";
import * as Sentry from "@sentry/react";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/stores/authStore";

interface BugReportState {
  isOpen: boolean;
  isSubmitting: boolean;
  screenshotUrl: string | null;
  screenshotBlob: Blob | null;
  success: boolean;
  error: string | null;
}

export function useBugReport() {
  const [state, setState] = useState<BugReportState>({
    isOpen: false,
    isSubmitting: false,
    screenshotUrl: null,
    screenshotBlob: null,
    success: false,
    error: null,
  });

  const user = useAuthStore((s) => s.user);

  const open = useCallback(async () => {
    let url: string | null = null;
    let blob: Blob | null = null;
    try {
      const canvas = await html2canvas(document.body, {
        scale: 0.5,
        logging: false,
        useCORS: true,
      });
      blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob((b) => resolve(b), "image/png", 0.7)
      );
      if (blob) {
        url = URL.createObjectURL(blob);
      }
    } catch {
      // Screenshot non disponible
    }
    setState({
      isOpen: true,
      isSubmitting: false,
      screenshotUrl: url,
      screenshotBlob: blob,
      success: false,
      error: null,
    });
  }, []);

  const close = useCallback(() => {
    if (state.screenshotUrl) {
      URL.revokeObjectURL(state.screenshotUrl);
    }
    setState({
      isOpen: false,
      isSubmitting: false,
      screenshotUrl: null,
      screenshotBlob: null,
      success: false,
      error: null,
    });
  }, [state.screenshotUrl]);

  const submit = useCallback(
    async (description: string) => {
      setState((s) => ({ ...s, isSubmitting: true, error: null }));

      try {
        const screenshotPath = await uploadScreenshot(state.screenshotBlob, user?.id);

        const report = {
          user_id: user?.id ?? null,
          user_email: user?.email ?? null,
          description,
          screenshot_path: screenshotPath,
          app_version: "1.0.0",
          platform: "web",
          device_info: {
            userAgent: navigator.userAgent,
            screenWidth: window.innerWidth,
            screenHeight: window.innerHeight,
            language: navigator.language,
          },
          current_route: window.location.pathname,
          sentry_event_id: Sentry.lastEventId() ?? null,
          app_name: "redi",
        };

        const { error: insertError } = await supabase
          .from("bug_reports")
          .insert(report);

        if (insertError) throw insertError;

        // Notification Discord (fire-and-forget)
        try {
          const fnUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-bug-report`;
          const { data: { session } } = await supabase.auth.getSession();
          fetch(fnUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session?.access_token ?? ""}`,
            },
            body: JSON.stringify(report),
          });
        } catch {
          // Non bloquant
        }

        setState((s) => ({ ...s, isSubmitting: false, success: true }));
      } catch {
        setState((s) => ({
          ...s,
          isSubmitting: false,
          error: "submit_failed",
        }));
      }
    },
    [state.screenshotBlob, user]
  );

  return { ...state, open, close, submit };
}

async function uploadScreenshot(
  blob: Blob | null,
  userId: string | undefined
): Promise<string | null> {
  if (!blob || !userId) return null;

  try {
    const filename = `${userId}/${Date.now()}.png`;

    const { error } = await supabase.storage
      .from("bug-screenshots")
      .upload(filename, blob, { contentType: "image/png" });

    if (error) return null;
    return filename;
  } catch {
    return null;
  }
}
